

import React, { useState, useEffect, useRef } from 'react';
import { ChatMessage, DiscussionThread, ArchiveFile, DBRow } from '../types';
import { chatWithArchive, generateChatTitle } from '../services/geminiService';
import { notifyTTSStarted, subscribeOtherTabTTS } from '../services/ttsChannel';

interface ChatPageProps {
  user: any;
  activeThreadId?: string;
  threads: DiscussionThread[];
  onSaveThread: (thread: DiscussionThread) => void;
  onNewChat: () => void;
  allFiles: ArchiveFile[];
  onFileSelect: (file: ArchiveFile, highlight?: string) => void;
  initialQuery?: string;
  localDB: Map<string, DBRow>;
  guestUsage: number;
  onIncrementGuestUsage: (amount: number) => void;
  onIncrementUsage: (amount: number) => void;
  onNavigateToSettings: () => void;
  aiUsage?: { dailyFreeUsed: number; freeLimit: number; credits: number };
  onSignIn: () => void;
  bypassBilling?: boolean;
}

const ChatPage: React.FC<ChatPageProps> = ({ 
  user,
  activeThreadId, 
  threads, 
  onSaveThread,
  onNewChat,
  onFileSelect,
  allFiles,
  initialQuery,
  localDB,
  guestUsage,
  onIncrementGuestUsage,
  onIncrementUsage,
  onNavigateToSettings,
  aiUsage,
  onSignIn,
  bypassBilling = false
}) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [dailyMessagesUsed, setDailyMessagesUsed] = useState(aiUsage?.dailyFreeUsed || 0); 
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Sync counter from localStorage or aiUsage on load
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const lastDate = localStorage.getItem('lastUsageDate');
    
    if (lastDate !== today) {
        setDailyMessagesUsed(0);
        localStorage.setItem('dailyMessagesUsed', '0');
        localStorage.setItem('lastUsageDate', today);
    } else {
        const saved = localStorage.getItem('dailyMessagesUsed');
        const firestoreValue = aiUsage?.dailyFreeUsed || 0;
        const localValue = saved ? parseInt(saved, 10) : 0;
        setDailyMessagesUsed(Math.max(localValue, firestoreValue));
    }
  }, [aiUsage]);

  // Update counter function
  const incrementDailyUsage = () => {
    const newValue = dailyMessagesUsed + 1;
    setDailyMessagesUsed(newValue);
    localStorage.setItem('dailyMessagesUsed', newValue.toString());
    localStorage.setItem('lastUsageDate', new Date().toISOString().split('T')[0]);
  };

  const isProcessing = useRef(false);
  const lastThreadId = useRef<string | undefined>(undefined);
  
  // State for TTS
  const [speakingMessageIndex, setSpeakingMessageIndex] = useState<number | null>(null);
  const [speakingParagraphIndex, setSpeakingParagraphIndex] = useState<number | null>(null);
  const stopSpeakingRef = useRef(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  
  // Track if initial query has been processed to prevent loop
  const hasProcessedInitialQuery = useRef(false);

  useEffect(() => {
    if (activeThreadId) {
      const thread = threads.find(t => t.id === activeThreadId);
      if (thread) {
          // Only overwrite history if the thread ID changed or if we have no history
          if (activeThreadId !== lastThreadId.current || history.length === 0) {
              setHistory(thread.history);
          }
      }
      // Reset this when switching threads
      hasProcessedInitialQuery.current = true; 
    } else {
      setHistory([]);
      // New chat implies we might need to process initial query
      hasProcessedInitialQuery.current = false;
    }
    lastThreadId.current = activeThreadId;
  }, [activeThreadId, threads]);

  // Effect to handle Initial Query auto-send
  useEffect(() => {
      if (initialQuery && !activeThreadId && !hasProcessedInitialQuery.current && !isTyping) {
          hasProcessedInitialQuery.current = true;
          handleAutoSubmit(initialQuery);
      }
  }, [initialQuery, activeThreadId]);

  useEffect(() => { 
    // Small delay to ensure layout has settled
    const timer = setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [history, isTyping]);

  useEffect(() => {
      // Load voices for TTS
      const updateVoices = () => {
          setVoices(window.speechSynthesis.getVoices());
      };
      updateVoices();
      window.speechSynthesis.onvoiceschanged = updateVoices;

      const unsubscribeTTS = subscribeOtherTabTTS(() => {
          stopSpeaking();
      });

      return () => {
          window.speechSynthesis.onvoiceschanged = null;
          unsubscribeTTS();
      };
  }, []);

  // If user is not authenticated and bypassBilling is false, render sign-in screen
  if (!user && !bypassBilling) {
    return (
      <div className="flex-1 flex flex-col h-full bg-[#fcfbf7] dark:bg-stone-900 overflow-y-auto">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto">
          <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-6 shadow-sm border border-amber-200/50 dark:border-amber-700/30 animate-pulse">
            <i className="fa-solid fa-wand-magic-sparkles text-3xl text-amber-600 dark:text-amber-500"></i>
          </div>
          <h2 className="text-3xl font-serif font-bold text-stone-800 dark:text-stone-100 mb-3">Consult the Librarian</h2>
          <p className="text-stone-600 dark:text-stone-400 mb-8 leading-relaxed text-sm md:text-base">
            The AI Librarian is available exclusively to registered members. 
            Sign in to access the collective wisdom of the Edgar Cayce digital archive, get 10 free daily messages, and save your chat history.
          </p>
          <button 
            type="button"
            onClick={onSignIn}
            className="px-8 py-3 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white font-serif font-semibold rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0 text-base"
          >
            <i className="fa-solid fa-arrow-right-to-bracket mr-2"></i>
            Sign In / Register
          </button>
        </div>
      </div>
    );
  }

  const handleAutoSubmit = async (query: string) => {
      if (isProcessing.current) return;
      isProcessing.current = true;
      
      // Optimistic Update
      const userMsg: ChatMessage = { role: 'user', text: query };
      const newHistory = [userMsg];
      setHistory(newHistory);
      setIsTyping(true);

      try {
          const aiResponse = await chatWithArchive(query, [], localDB);
          
          const aiMsg: ChatMessage = { role: 'ai', text: aiResponse };
          const updatedHistory = [...newHistory, aiMsg];
          setHistory(updatedHistory);
          
          const title = await generateChatTitle(updatedHistory);
          onSaveThread({ id: `chat-${Date.now()}`, query: userMsg.text, title, history: updatedHistory, lastUpdated: Date.now() });
      } catch (err: any) { 
          const errorMessage = err.message || "Connection unstable. I could not access the records.";
          setHistory(prev => [...prev, { role: 'ai', text: errorMessage }]); 
      } finally { 
          setIsTyping(false); 
          isProcessing.current = false;
      }
  };

  const handleSubmit = async (e: React.FormEvent | React.KeyboardEvent) => {
    if (e) e.preventDefault();
    if (e && 'stopPropagation' in e) e.stopPropagation();
    
    if (!input.trim() || isTyping || isProcessing.current) return;
    
    // Check usage and payment method
    let isBlocked = false;
    let blockMessage = '';

    if (!bypassBilling) {
      if (!user) {
          if (guestUsage >= 0.5) {
              isBlocked = true;
              blockMessage = 'Free quota of $0.50 exceeded. Please log in to continue.';
          }
      } else {
          // Only require payment method if they have exceeded the 10 daily free messages
          if (dailyMessagesUsed >= 10) {
              const totalAllowance = (user.monthlySpendCap || 0) + (user.topupBalance || 0);
              const hasBalance = (user.currentUsage < totalAllowance) || user.payAsYouGo;

              if ((!user.monthlySpendCap || user.monthlySpendCap <= 0) && !user.topupBalance && !user.payAsYouGo) {
                  isBlocked = true;
                  blockMessage = 'You have used your 10 free daily messages. Please set up a payment method in Account Settings to continue.';
              } else if (!hasBalance) {
                  isBlocked = true;
                  blockMessage = 'Monthly Usage Limit Reached. Please top up in Account Settings.';
              }
          }
      }
    }

    if (isBlocked) {
        alert(blockMessage);
        return;
    }

    isProcessing.current = true;
    
    // Stop any ongoing speech when sending new message
    stopSpeaking();

    // Optimistic Update
    const userMsg: ChatMessage = { role: 'user', text: input };
    const newHistory = [...history, userMsg];
    setHistory(newHistory); 
    setInput(''); 
    setIsTyping(true);
    
    try {
      // Pass the *previous* history so the service can construct the chain correctly.
      // input is the *new* query.
      const aiResponse = await chatWithArchive(input, history, localDB);
      
      const aiMsg: ChatMessage = { role: 'ai', text: aiResponse };
      const updatedHistory = [...newHistory, aiMsg];
      setHistory(updatedHistory);
      
      const title = history.length === 0 ? await generateChatTitle(updatedHistory) : threads.find(t => t.id === activeThreadId)?.title || "Discussion";
      onSaveThread({ id: activeThreadId || `chat-${Date.now()}`, query: userMsg.text, title, history: updatedHistory, lastUpdated: Date.now() });
    } catch (err: any) { 
        // Fallback catch, though chatWithArchive handles most errors now
        const errorMessage = err.message || "Connection unstable. I could not access the records.";
        setHistory(prev => [...prev, { role: 'ai', text: errorMessage }]); 
    } finally { 
        setIsTyping(false); 
        isProcessing.current = false;
    }
  };

  function stopSpeaking() {
      stopSpeakingRef.current = true;
      window.speechSynthesis.cancel();
      setSpeakingMessageIndex(null);
      setSpeakingParagraphIndex(null);
  }

  const getBestVoice = () => {
      let currentLang = document.documentElement.lang || 'en';
      const match = document.cookie.match(/googtrans=\/auto\/([a-zA-Z-]+)/);
      if (match && match[1]) {
          currentLang = match[1];
      }

      const matchingVoices = voices.filter(v => v.lang.startsWith(currentLang.split('-')[0]));
      
      // Sort: Microsoft > Google, Natural/Online > Standard
      matchingVoices.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const scoreA = (aName.includes('microsoft') ? 2 : 0) + (aName.includes('natural') ? 1 : 0) + (aName.includes('online') ? 0.5 : 0);
          const scoreB = (bName.includes('microsoft') ? 2 : 0) + (bName.includes('natural') ? 1 : 0) + (bName.includes('online') ? 0.5 : 0);
          return scoreB - scoreA;
      });
      return matchingVoices.length > 0 ? matchingVoices[0] : null;
  };

  const normalizeTextForSpeech = (text: string): string => {
      if (!text) return '';
      // 1. Fix reading numbers: 0065-0003 -> 65-3
      let normalized = text.replace(/\b0+(\d+)-0+(\d+)\b/g, '$1-$2');
      normalized = normalized.replace(/\b0+([1-9]\d*)\b/g, '$1'); // 007 -> 7
      
      // 2. Fix all-caps words (2+ chars) to lowercase to prevent spelling out (e.g. "IN", "THE")
      normalized = normalized.replace(/\b([A-Z]{2,})\b/g, (match) => {
          return match.toLowerCase();
      });
      return normalized;
  };

  const speakSequence = (lines: string[], index: number, msgIndex: number) => {
      if (stopSpeakingRef.current) return;
      
      if (index >= lines.length) {
          setSpeakingMessageIndex(null);
          setSpeakingParagraphIndex(null);
          return;
      }

      const line = lines[index].trim();
      
      // Skip empty lines or table separators
      if (!line || line.match(/^\|?[\s:-]+\|/)) {
          speakSequence(lines, index + 1, msgIndex);
          return;
      }

      setSpeakingParagraphIndex(index);

      // Scroll into view
      const elementId = `msg-${msgIndex}-line-${index}`;
      const el = document.getElementById(elementId);
      if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // Fetch translated text from DOM if available, otherwise fallback to cleanText
      let textToSpeak = line
          .replace(/[*#`_]/g, '')
          .replace(/\[\[.*?\]\]/g, '')
          .replace(/^\||\|$/g, '') // Remove start/end pipes
          .trim();

      if (el && el.innerText) {
          textToSpeak = el.innerText.replace(/\[\[.*?\]\]/g, '').trim();
      }

      textToSpeak = normalizeTextForSpeech(textToSpeak);

      if (!textToSpeak) {
          speakSequence(lines, index + 1, msgIndex);
          return;
      }

      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      
      let currentLang = document.documentElement.lang || 'en';
      const match = document.cookie.match(/googtrans=\/auto\/([a-zA-Z-]+)/);
      if (match && match[1]) {
          currentLang = match[1];
      }
      utterance.lang = currentLang;
      
      const voice = getBestVoice();
      if (voice) utterance.voice = voice;

      utterance.onend = () => {
          if (!stopSpeakingRef.current) {
              speakSequence(lines, index + 1, msgIndex);
          }
      };

      utterance.onerror = (e) => {
          if (e.error === 'interrupted' || e.error === 'canceled') {
              return;
          }
          console.error("TTS Error:", e);
          if (!stopSpeakingRef.current) {
              speakSequence(lines, index + 1, msgIndex);
          }
      };

      window.speechSynthesis.speak(utterance);
  };

  const handleSpeak = (text: string, msgIndex: number) => {
      if (speakingMessageIndex === msgIndex) {
          stopSpeaking();
      } else {
          stopSpeaking();
          notifyTTSStarted();
          stopSpeakingRef.current = false;
          setSpeakingMessageIndex(msgIndex);
          // Split exactly as renderMessageText does
          const lines = text.split('\n');
          speakSequence(lines, 0, msgIndex);
      }
  };

  const handleCopy = async (text: string) => {
      // Generate HTML and Plain Text for clipboard
      let html = text;
      let plain = text;

      // Process citations [[Reading ID | "quote"]]
      const citationRegex = /\[\[([\s\S]+?)\|([\s\S]*?)\]\]/g;
      
      html = html.replace(citationRegex, (match, id, quote, offset) => {
          let readingId = id.trim();
          let highlightText = quote.trim();
          let normalizedId = readingId;
          
          const rMatch = readingId.match(/Reading\s*(\d+)-(\d+)/i);
          if (rMatch) {
              normalizedId = `${rMatch[1].padStart(4, '0')}-${rMatch[2].padStart(4, '0')}`;
          }
          
          if ((highlightText.startsWith('"') && highlightText.endsWith('"')) || 
              (highlightText.startsWith("'") && highlightText.endsWith("'"))) {
              highlightText = highlightText.slice(1, -1);
          }
          
          const href = `${window.location.origin}/readings/${encodeURIComponent(normalizedId)}?highlight=${encodeURIComponent(highlightText)}`;
          
          let shouldRenderQuoteText = true;
          const isParagraphBookmark = /^\s*[\(\[]?\d+[\)\]\.]?\s*$/.test(highlightText);
          if (isParagraphBookmark) {
              shouldRenderQuoteText = false;
          } else {
              const precedingText = html.slice(0, offset);
              const cleanPrev = precedingText.toLowerCase().replace(/["'\s\p{P}]/gu, "");
              const cleanQuote = highlightText.toLowerCase().replace(/["'\s\p{P}]/gu, "");
              if (cleanPrev.includes(cleanQuote)) {
                  shouldRenderQuoteText = false;
              }
          }
          
          if (shouldRenderQuoteText) {
              return `"${highlightText}" <a href="${href}">${readingId}</a>`;
          } else {
              return `<a href="${href}">${readingId}</a>`;
          }
      });

      plain = plain.replace(citationRegex, (match, id, quote, offset) => {
          let highlightText = quote.trim();
          if ((highlightText.startsWith('"') && highlightText.endsWith('"')) || 
              (highlightText.startsWith("'") && highlightText.endsWith("'"))) {
              highlightText = highlightText.slice(1, -1);
          }
          
          let shouldRenderQuoteText = true;
          const isParagraphBookmark = /^\s*[\(\[]?\d+[\)\]\.]?\s*$/.test(highlightText);
          if (isParagraphBookmark) {
              shouldRenderQuoteText = false;
          } else {
              const precedingText = plain.slice(0, offset);
              const cleanPrev = precedingText.toLowerCase().replace(/["'\s\p{P}]/gu, "");
              const cleanQuote = highlightText.toLowerCase().replace(/["'\s\p{P}]/gu, "");
              if (cleanPrev.includes(cleanQuote)) {
                  shouldRenderQuoteText = false;
              }
          }
          
          if (shouldRenderQuoteText) {
              return `"${highlightText}" [${id.trim()}]`;
          } else {
              return `[${id.trim()}]`;
          }
      });

      // Process bold
      html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      plain = plain.replace(/\*\*(.*?)\*\*/g, '$1');

      // Process italic
      html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
      plain = plain.replace(/\*(.*?)\*/g, '$1');

      // Process newlines
      html = html.replace(/\n/g, '<br>');

      try {
          const clipboardItem = new ClipboardItem({
              'text/plain': new Blob([plain], { type: 'text/plain' }),
              'text/html': new Blob([html], { type: 'text/html' })
          });
          await navigator.clipboard.write([clipboardItem]);
      } catch (e) {
          // Fallback if ClipboardItem is not supported
          navigator.clipboard.writeText(plain);
      }
  };

  // Process text for bold, italic, and links
  const processContent = (text: string) => {
    // Robust regex for splitting: Matches [[ ID | Content ]] including newlines
    const splitRegex = /(\[\[[^|]+?\|[\s\S]*?\]\])/g;
    const parts = text.split(splitRegex);

    return parts.map((part, pIdx) => {
        const citationMatch = part.match(/^\[\[([\s\S]+?)\|([\s\S]*?)\]\]$/);
        
        if (citationMatch) {
            let readingId = citationMatch[1].trim();
            let rawQuote = citationMatch[2].trim();
            let highlightText = rawQuote;
            let normalizedId = readingId;
            
            const rMatch = readingId.match(/Reading\s*(\d+)-(\d+)/i);
            if (rMatch) {
                normalizedId = `${rMatch[1].padStart(4, '0')}-${rMatch[2].padStart(4, '0')}`;
            }
            
            if ((highlightText.startsWith('"') && highlightText.endsWith('"')) || 
                (highlightText.startsWith("'") && highlightText.endsWith("'"))) {
                highlightText = highlightText.slice(1, -1);
            }
            
            const href = `/readings/${encodeURIComponent(normalizedId)}?highlight=${encodeURIComponent(highlightText)}`;

            let shouldRenderQuoteText = true;
            const isParagraphBookmark = /^\s*[\(\[]?\d+[\)\]\.]?\s*$/.test(highlightText);
            if (isParagraphBookmark) {
                shouldRenderQuoteText = false;
            } else {
                const prevPart = pIdx > 0 ? parts[pIdx - 1] : "";
                if (prevPart) {
                    const cleanPrev = prevPart.toLowerCase().replace(/["'\s\p{P}]/gu, "");
                    const cleanQuote = highlightText.toLowerCase().replace(/["'\s\p{P}]/gu, "");
                    if (cleanPrev.includes(cleanQuote) || (cleanQuote && cleanPrev.slice(-cleanQuote.length) === cleanQuote)) {
                        shouldRenderQuoteText = false;
                    }
                }
            }

            return (
                <span key={pIdx}>
                    {shouldRenderQuoteText && (
                        <span className="italic text-stone-700 dark:text-stone-300">"{highlightText}"</span>
                    )}
                    <a 
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800/60 transition-all font-bold text-xs mx-1 cursor-pointer border border-amber-200 dark:border-amber-800 no-underline align-middle"
                        title="Open Reading Reference"
                    >
                        <i className="fa-solid fa-book-open text-[10px]"></i>
                        {readingId}
                    </a>
                </span>
            );
        }

        // Format bold/italic/inline quotes
        const textParts = part.split(/(\*\*.*?\*\*|\*[^*]+?\*|"[^"\n]{2,}"|“[^”\n]{2,}”)/g);
        return textParts.map((tPart, i) => {
            if (tPart.startsWith('**') && tPart.endsWith('**')) {
                return <strong key={`${pIdx}-${i}`} className="font-bold text-stone-900 dark:text-white">{tPart.slice(2, -2)}</strong>;
            }
            if (tPart.startsWith('*') && tPart.endsWith('*')) {
                return <em key={`${pIdx}-${i}`} className="italic text-stone-600 dark:text-stone-300">{tPart.slice(1, -1)}</em>;
            }
            if ((tPart.startsWith('"') && tPart.endsWith('"')) || (tPart.startsWith('“') && tPart.endsWith('”'))) {
                return <span key={`${pIdx}-${i}`} className="italic font-serif text-stone-800 dark:text-stone-200">{tPart}</span>;
            }
            return <span key={`${pIdx}-${i}`}>{tPart}</span>;
        });
    });
  };

  const renderMessageText = (text: string, isAi: boolean, msgIndex: number) => {
      // Fallback: If text looks like the specific JSON bug from Editor mode
      if (text.trim().startsWith('{') && text.includes('"summary":')) {
          try {
              const json = JSON.parse(text);
              if (json.summary) {
                  return (
                      <div>
                          <p className="mb-2 text-amber-600 font-bold text-xs uppercase tracking-wider">[Restored from Data Structure]</p>
                          <p>{json.summary}</p>
                          {json.blocks && (
                              <div className="mt-2 pl-2 border-l-2 border-stone-200 dark:border-stone-700 text-xs text-stone-500">
                                  {json.blocks.map((b: any, i: number) => (
                                      b.type === 'text' ? <div key={i} dangerouslySetInnerHTML={{__html: b.content}} /> : <p key={i}>[Image]</p>
                                  ))}
                              </div>
                          )}
                      </div>
                  );
              }
          } catch (e) {
              // Not valid JSON, render as is
          }
      }

      // Pre-process: Collapse newlines inside citations to prevent line-splitting logic from breaking them
      // This allows citations that span multiple lines to be correctly identified as a single token.
      const safeText = text.replace(/(\[\[[\s\S]*?\]\])/g, (match) => {
          return match.replace(/\n/g, ' ');
      });

      if (!isAi) {
          return safeText.split('\n').map((p, idx) => <p key={idx} className="mb-2 last:mb-0 min-h-[1em]">{p}</p>);
      }

      const lines = safeText.split('\n');
      const renderedElements: React.ReactNode[] = [];

      for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const trimmed = line.trim();
          const id = `msg-${msgIndex}-line-${i}`;

          // Highlighting Logic
          const isHighlighted = speakingMessageIndex === msgIndex && speakingParagraphIndex === i;
          const highlightClass = isHighlighted 
              ? "bg-amber-100 dark:bg-amber-900/40 rounded px-2 -mx-2 transition-all duration-300 border-l-4 border-amber-500 shadow-sm" 
              : "border-l-4 border-transparent px-2 -mx-2";

          if (!trimmed) {
              renderedElements.push(<div key={i} className="h-2"></div>);
              continue;
          }

          // --- TABLE DETECTION ---
          // Check if current line has pipes and looks like a header or row
          if (trimmed.includes('|') && i + 1 < lines.length) {
              const nextLine = lines[i+1].trim();
              // Regex matches markdown table separator row: |---| or | :--- |
              if (nextLine.match(/^\|?[\s:-]+\|[\s:-]+\|/)) {
                  const tableRows: string[] = [];
                  const tableHeader = trimmed;
                  
                  // Consume lines until we hit something that isn't a table row
                  let j = i + 2; 
                  while (j < lines.length && lines[j].trim().includes('|')) {
                      tableRows.push(lines[j].trim());
                      j++;
                  }

                  // Render the table
                  renderedElements.push(
                      <div key={`table-${i}`} id={id} className={`${highlightClass} overflow-x-auto my-4 rounded-lg border border-stone-200 dark:border-stone-700 shadow-sm`}>
                          <table className="min-w-full text-sm divide-y divide-stone-200 dark:divide-stone-700">
                              <thead className="bg-stone-50 dark:bg-stone-800">
                                  <tr>
                                      {tableHeader.split('|').map((cell, cIdx) => {
                                          const cellContent = cell.trim();
                                          // Skip empty cells caused by leading/trailing pipes
                                          if (!cellContent && (cIdx === 0 || cIdx === tableHeader.split('|').length - 1)) return null; 
                                          return (
                                              <th key={cIdx} className="px-4 py-3 text-left font-bold text-stone-700 dark:text-stone-200 uppercase tracking-wider text-xs border-r border-stone-200 dark:border-stone-700 last:border-0 whitespace-nowrap">
                                                  {cellContent}
                                              </th>
                                          );
                                      })}
                                  </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-stone-800/50 divide-y divide-stone-100 dark:divide-stone-700">
                                  {tableRows.map((row, rIdx) => (
                                      <tr key={rIdx} className="hover:bg-stone-50 dark:hover:bg-stone-700/30 transition-colors">
                                          {row.split('|').map((cell, cIdx) => {
                                              const cellContent = cell.trim();
                                              if (!cellContent && (cIdx === 0 || cIdx === row.split('|').length - 1)) return null;
                                              return (
                                                  <td key={cIdx} className="px-4 py-3 text-stone-600 dark:text-stone-300 border-r border-stone-100 dark:border-stone-700 last:border-0 leading-relaxed">
                                                      {processContent(cellContent)}
                                                  </td>
                                              );
                                          })}
                                      </tr>
                                  ))}
                              </tbody>
                          </table>
                      </div>
                  );

                  // Fast forward the loop index past the table
                  i = j - 1; 
                  continue;
              }
          }

          // Headers
          if (trimmed.startsWith('### ')) {
              renderedElements.push(
                  <h3 id={id} key={i} className={`${highlightClass} text-lg font-serif font-bold text-amber-800 dark:text-amber-500 mt-6 mb-2 pb-1 border-b border-stone-100 dark:border-stone-700`}>
                      {processContent(trimmed.slice(4))}
                  </h3>
              );
              continue;
          }
          if (trimmed.startsWith('## ')) {
              renderedElements.push(
                  <h2 id={id} key={i} className={`${highlightClass} text-xl font-serif font-bold text-stone-800 dark:text-stone-100 mt-8 mb-4`}>
                      {processContent(trimmed.slice(3))}
                  </h2>
              );
              continue;
          }

          // Blockquotes
          if (trimmed.startsWith('> ')) {
              renderedElements.push(
                  <blockquote id={id} key={i} className={`${isHighlighted ? 'ring-2 ring-amber-400' : ''} border-l-4 border-amber-400 pl-4 py-3 my-4 bg-amber-50 dark:bg-amber-900/10 italic text-stone-700 dark:text-stone-300 rounded-r-lg shadow-sm transition-all duration-300`}>
                      {processContent(trimmed.slice(2))}
                  </blockquote>
              );
              continue;
          }

          // List Items (Bullet)
          if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
              renderedElements.push(
                  <div id={id} key={i} className={`${highlightClass} flex gap-3 ml-2 md:ml-4 mb-2`}>
                      <span className="text-amber-500 mt-2 text-[6px] shrink-0"><i className="fa-solid fa-circle"></i></span>
                      <span className="leading-relaxed text-stone-700 dark:text-stone-300">
                          {processContent(trimmed.slice(2))}
                      </span>
                  </div>
              );
              continue;
          }

          // List Items (Numbered)
          const numberMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
          if (numberMatch) {
               renderedElements.push(
                  <div id={id} key={i} className={`${highlightClass} flex gap-3 ml-2 md:ml-4 mb-2`}>
                      <span className="font-bold text-amber-600 dark:text-amber-500 min-w-[1.2em] text-right">{numberMatch[1]}.</span>
                      <span className="leading-relaxed text-stone-700 dark:text-stone-300">
                          {processContent(numberMatch[2])}
                      </span>
                  </div>
              );
              continue;
          }

          // Regular Paragraph
          renderedElements.push(
              <p id={id} key={i} className={`${highlightClass} leading-relaxed mb-4 text-stone-800 dark:text-stone-300`}>
                  {processContent(trimmed)}
              </p>
          );
      }

      return (
          <div className="space-y-1">
              {renderedElements}
          </div>
      );
  };

  return (
    <div className="flex flex-col h-full bg-[#fbfaf6] dark:bg-stone-900 transition-colors duration-300">
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-8 space-y-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {history.length === 0 && !isTyping && (
             <div className="py-20 text-center space-y-6 animate-in fade-in zoom-in-95 duration-700">
                <div className="w-24 h-24 mx-auto rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center border border-amber-200 dark:border-amber-800 shadow-xl"><i className="fa-solid fa-book-open-reader text-4xl text-amber-600"></i></div>
                <h2 className="text-3xl font-serif font-bold text-stone-800 dark:text-stone-100">Consult the Librarian</h2>
                <p className="text-stone-500 dark:text-stone-400 max-w-md mx-auto">Access the collective wisdom of the Edgar Cayce digital archive through natural dialogue.</p>
             </div>
          )}
          {history.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 group relative`}>
              <div className={`flex flex-col max-w-[95%] md:max-w-[85%] p-4 md:p-6 rounded-2xl shadow-sm ${msg.role === 'user' ? 'bg-amber-600 text-white rounded-tr-none' : 'bg-white dark:bg-stone-800 border dark:border-stone-700 text-stone-800 dark:text-stone-200 rounded-tl-none font-serif leading-relaxed'}`}>
                <div className="flex-1">
                    {renderMessageText(msg.text, msg.role === 'ai', i)}
                </div>
                
                {/* Actions: Sticky Bottom */}
                {msg.role === 'ai' && (
                    <div className="sticky bottom-2 self-end mt-2 flex items-center gap-1 bg-stone-50 dark:bg-stone-900/50 backdrop-blur rounded-lg border border-stone-100 dark:border-stone-700 p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10 shadow-sm">
                        <button 
                            onClick={() => handleSpeak(msg.text, i)} 
                            className={`w-7 h-7 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors ${speakingMessageIndex === i ? 'text-amber-500 animate-pulse' : 'text-stone-400 dark:text-stone-500'}`}
                            title="Read Aloud"
                        >
                            <i className={`fa-solid ${speakingMessageIndex === i ? 'fa-stop' : 'fa-volume-high'} text-xs`}></i>
                        </button>
                        <div className="w-px h-3 bg-stone-200 dark:bg-stone-700"></div>
                        <button 
                            onClick={() => handleCopy(msg.text)} 
                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-400 dark:text-stone-500 transition-colors"
                            title="Copy"
                        >
                            <i className="fa-regular fa-copy text-xs"></i>
                        </button>
                    </div>
                )}
              </div>
            </div>
          ))}
          {isTyping && <div className="flex justify-start"><div className="bg-white dark:bg-stone-800 p-4 rounded-2xl shadow-sm animate-pulse text-[10px] font-bold text-stone-400 uppercase tracking-widest">Consulting Records...</div></div>}
          <div ref={chatEndRef} className="h-10" />
        </div>
      </div>
      <div className="p-4 md:p-6 bg-[#f2efe4]/80 dark:bg-[#0f1521]/80 backdrop-blur-md border-t border-stone-200 dark:border-stone-800 shrink-0">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto relative">
          {user && user.monthlySpendCap !== undefined && user.currentUsage !== undefined && (user.currentUsage >= ((user.monthlySpendCap || 0) + (user.topupBalance || 0))) && !user.payAsYouGo ? (
            <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl py-4 px-6 text-center animate-in fade-in slide-in-from-bottom-2">
                <p className="text-red-600 dark:text-red-400 font-bold text-sm">
                    <i className="fa-solid fa-triangle-exclamation mr-2"></i>
                    Monthly Usage Limit Reached
                </p>
                <p className="text-[10px] text-red-500/70 mt-1 uppercase tracking-widest font-bold">
                    Please increase your spending cap in 
                    <button 
                        type="button" 
                        onClick={() => onNavigateToSettings()}
                        className="mx-1 underline hover:text-red-700 transition-colors"
                    >
                        Account Settings
                    </button>
                </p>
            </div>
          ) : (
            <>
              <div className="relative h-16 md:h-20">
                <textarea 
                  value={input} 
                  onChange={(e) => setInput(e.target.value)} 
                  onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }} 
                  placeholder="Ask the Librarian..." 
                  className="w-full bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-2xl py-4 pl-6 pr-14 focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 outline-none transition-all resize-none shadow-lg dark:text-white h-16 md:h-20" 
                />
                <button 
                  type="submit" 
                  disabled={!input.trim() || isTyping} 
                  className="absolute right-3 top-0 bottom-0 my-auto w-10 h-10 md:w-12 md:h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl shadow-md transition-all flex items-center justify-center disabled:opacity-30"
                >
                  <i className="fa-solid fa-paper-plane"></i>
                </button>
              </div>
              {!bypassBilling && (
              <div className="text-[10px] text-stone-500 mt-2 px-1 font-mono flex items-center gap-1">
                {Math.max(0, 10 - dailyMessagesUsed)} of 10 daily free messages remaining. 
                <div className="relative group">
                  <i className="fa-solid fa-circle-info text-stone-400 cursor-help"></i>
                  <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-stone-800 text-stone-100 text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    For further usage, please add a payment method in Settings or browse the indexed 'Readings' page.
                  </div>
                </div>
              </div>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChatPage;
